import { z } from 'zod';
import { TenantIdSchema, UserIdSchema } from './ids';
import { TimestampSchema } from './primitives';

/**
 * The client credential's vocabulary (docs/DECISIONS.md ZD-117).
 *
 * A person is a `users` row and nothing here changes that: the role, the
 * tenant and `disabled_at` are the record's (ZD-115 §2). What this module
 * names is what hangs off that row once the person can sign in by password —
 * a session, a one-time token — and the shape of what a request supplies to
 * sign in, accept an invitation or reset a password. The password *policy* is
 * here too, because `apps/web` will render the rule to a person before the API
 * refuses it, and one constant read by both is what keeps the two sentences
 * the same (`sds-13b`).
 *
 * The password *storage* format is not here. It lives beside the code that
 * hashes (`@zevik/shared`'s `tenancy/password.ts`), because a browser has no
 * business knowing it and this package ships to one (ZD-112).
 */

/**
 * NIST SP 800-63B-4 §3.1.1.2: a password that is the only authentication
 * factor is at least fifteen characters; no composition rules, no expiry. The
 * upper bound is on what is hashed, not on what is allowed — the standard asks
 * for at least sixty-four, and hashing a megabyte is a way to spend a Worker's
 * CPU budget on one request.
 */
export const PASSWORD_MIN_LENGTH = 15;
export const PASSWORD_MAX_LENGTH = 256;

export const PasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `a password must be at least ${String(PASSWORD_MIN_LENGTH)} characters`)
  .max(PASSWORD_MAX_LENGTH, `a password must be at most ${String(PASSWORD_MAX_LENGTH)} characters`);

/**
 * A session, as the record holds it. `session_id` is the SHA-256 of the token
 * the browser holds, so the row identifies the session without being able to
 * present it. Two lifetimes, both NIST SP 800-63B AAL2: `expires_at` is
 * absolute, `last_seen_at` carries the idle one.
 */
export const SessionSchema = z.object({
  session_id: z.string().regex(/^[0-9a-f]{64}$/u),
  tenant_id: TenantIdSchema,
  user_id: UserIdSchema,
  created_at: TimestampSchema,
  expires_at: TimestampSchema,
  last_seen_at: TimestampSchema,
  revoked_at: TimestampSchema.nullable(),
});
export type Session = z.infer<typeof SessionSchema>;

/**
 * Why a one-time token exists: an `invitation` gives a person the means to set
 * a first password; a `reset` replaces one. The two differ in who issues them
 * (an administrator or the bootstrap; the person themself) and how long they
 * live, and a token is only ever consumed for the purpose it was issued for.
 */
export const TokenPurposeSchema = z.enum(['invitation', 'reset']);
export type TokenPurpose = z.infer<typeof TokenPurposeSchema>;

export const CredentialTokenSchema = z.object({
  token_id: z.string().regex(/^[0-9a-f]{64}$/u),
  tenant_id: TenantIdSchema,
  user_id: UserIdSchema,
  purpose: TokenPurposeSchema,
  created_at: TimestampSchema,
  expires_at: TimestampSchema,
  consumed_at: TimestampSchema.nullable(),
  superseded_at: TimestampSchema.nullable(),
});
export type CredentialToken = z.infer<typeof CredentialTokenSchema>;

/** The token as it travels in a link: 32 random bytes, base64url, no padding. */
export const PresentedTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/u);

export const SignInInputSchema = z.object({
  email: z.email(),
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type SignInInput = z.infer<typeof SignInInputSchema>;

export const AcceptInvitationInputSchema = z.object({
  token: PresentedTokenSchema,
  password: PasswordSchema,
});
export type AcceptInvitationInput = z.infer<typeof AcceptInvitationInputSchema>;

export const ResetRequestInputSchema = z.object({
  email: z.email(),
});
export type ResetRequestInput = z.infer<typeof ResetRequestInputSchema>;

export const ResetPasswordInputSchema = z.object({
  token: PresentedTokenSchema,
  password: PasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordInputSchema>;

/**
 * The console's paths for the two links an email carries (ZD-117 §5). Decided
 * here, before `sds-13b` builds the pages, because a link in a sent email is a
 * commitment that outlives the deployment that sent it — and the API builds the
 * link while the console serves it, so one constant read by both is the
 * contract.
 */
export const INVITATION_PATH = '/invitation';
export const RESET_PATH = '/reset';
export const SIGN_IN_PATH = '/sign-in';
